import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { getCurrentUser } from '../utils/auth';
import { NAME_LIMITS } from '../utils/nameLimits';
import ResendVerification from '../components/ResendVerification';

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const inviteUrl = `/accept-invite?token=${encodeURIComponent(token || '')}`;

  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');

  const [invite, setInvite] = useState(null); // { email, group: { _id, name } }
  const [inviteError, setInviteError] = useState('');
  const [loadingInvite, setLoadingInvite] = useState(true);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  // Invited accounts must verify their email before signing in; { sent } tells whether the email went out
  const [needsVerification, setNeedsVerification] = useState(null);

  const currentUser = getCurrentUser();
  const loggedInAsInvitee = !!currentUser?.email && currentUser.email.toLowerCase() === invite?.email;

  // Check the invitation before showing the form
  useEffect(() => {
    if (!token) {
      setInviteError('This invitation link is missing its token.');
      setLoadingInvite(false);
      return;
    }

    let cancelled = false;
    const validate = async () => {
      try {
        const res = await api.get('/api/invites/validate', { params: { token } });
        if (!cancelled) setInvite(res.data);
      } catch (err) {
        if (!cancelled) {
          setInviteError(err.response?.data?.message || 'This invitation is invalid or has expired.');
        }
      } finally {
        if (!cancelled) setLoadingInvite(false);
      }
    };
    validate();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setFormError('');

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      // The email is taken from the invitation on the server, not from this form
      const res = await api.post('/api/users/register', {
        username,
        email: invite.email,
        password,
        inviteToken: token,
      });
      setNeedsVerification(res.data.emailVerificationRequired ? { sent: res.data.verificationEmailSent } : null);
      setRegistered(true);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Existing account, logged in with the invited email: join directly
  const handleJoin = async () => {
    setJoinError('');
    setJoining(true);
    try {
      const res = await api.post('/api/invites/join-group', { token });
      // Refreshed token includes the supplier attached by the invitation
      if (res.data.token) localStorage.setItem('token', res.data.token);
      // Open Search Groups on the joined group's supplier and group (same as InvitePage)
      const joinedGroup = res.data.group;
      const supplierId = joinedGroup?.supplierId && (joinedGroup.supplierId._id || joinedGroup.supplierId);
      if (joinedGroup?._id && supplierId) {
        navigate(`/GroupSearchAndJoin?supplierId=${supplierId}&groupId=${joinedGroup._id}`, { replace: true });
      } else {
        navigate('/GroupSearchAndJoin', { replace: true });
      }
    } catch (err) {
      setJoinError(err.response?.data?.message || 'Could not join the group. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  const renderExistingAccount = () => {
    if (loggedInAsInvitee) {
      return (
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Join "{invite.group.name}"</h2>
          <p className="text-sm text-gray-600 mb-4">You're logged in as {invite.email}.</p>
          {joinError && <p className="text-red-500 mb-4">{joinError}</p>}
          <button
            type="button"
            onClick={handleJoin}
            disabled={joining}
            className={`w-full py-2 rounded-md text-white transition duration-200 ${
              joining ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {joining ? 'Joining...' : 'Join group'}
          </button>
        </div>
      );
    }

    if (currentUser) {
      return (
        <div className="text-center">
          <p className="text-gray-700 mb-2">
            This invitation is for <strong>{invite.email}</strong>, but you're logged in as{' '}
            <strong>{currentUser.email}</strong>.
          </p>
          <Link
            to="/login"
            state={{ from: inviteUrl }}
            className="text-blue-600 underline"
          >
            Log in with {invite.email} to join
          </Link>
        </div>
      );
    }

    return (
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">Join "{invite.group.name}"</h2>
        <p className="text-gray-700 mb-4">
          An account already exists for <strong>{invite.email}</strong>. Log in to join the group.
        </p>
        <Link
          to="/login"
          state={{ from: inviteUrl }}
          className="inline-block w-full py-2 rounded-md text-white bg-green-600 hover:bg-green-700"
        >
          Log in to join
        </Link>
      </div>
    );
  };

  const renderBody = () => {
    if (loadingInvite) {
      return <p className="text-center text-gray-600">Checking your invitation...</p>;
    }

    if (inviteError) {
      return (
        <div className="text-center">
          <p className="text-red-500 mb-4">{inviteError}</p>
          <p className="text-sm text-gray-600">
            Ask the group organizer to send a new invitation.
          </p>
        </div>
      );
    }

    if (invite.accountExists) {
      return renderExistingAccount();
    }

    if (registered && needsVerification) {
      return (
        <div className="text-center">
          <p className="text-green-600 mb-2">Your account was created. Check {invite.email} for a verification link, then log in.</p>
          {!needsVerification.sent && <p className="text-red-500 mb-2">We could not send the verification email. Please try again.</p>}
          <ResendVerification email={invite.email} />
          <Link to="/login" className="block mt-4 text-blue-600 underline">Log in</Link>
        </div>
      );
    }

    if (registered) {
      // Same destination as joining an existing account: the invited group, with its supplier selected
      const searchUrl = invite.group.supplierId
        ? `/GroupSearchAndJoin?supplierId=${invite.group.supplierId}&groupId=${invite.group._id}`
        : '/GroupSearchAndJoin';
      return (
        <div className="text-center">
          <p className="text-green-600 mb-2">
            Your account is ready and you have joined "{invite.group.name}".
          </p>
          {/* First login after an invite lands on the group search page, then the normal home landing */}
          <Link
            to="/login"
            state={{ from: searchUrl }}
            className="text-blue-600 underline"
          >
            Log in to continue
          </Link>
        </div>
      );
    }

    return (
      <>
        <h2 className="text-xl font-semibold mb-1">Join "{invite.group.name}"</h2>
        <p className="text-sm text-gray-600 mb-4">
          You were invited to Qercha. Create your account to join the group.
        </p>

        {formError && <div className="text-red-500 text-center mb-4">{formError}</div>}

        <form onSubmit={handleRegister} className="space-y-4" noValidate>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Email</label>
            <input
              type="email"
              value={invite.email}
              readOnly
              className="w-full px-3 py-2 border rounded-md bg-gray-200 cursor-not-allowed"
            />
          </div>

          <div>
            <label htmlFor="invite-username" className="block text-sm text-gray-600 mb-1">Username</label>
            <input
              id="invite-username"
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              maxLength={NAME_LIMITS.username.max}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="username"
            />
          </div>

          <div>
            <label htmlFor="invite-password" className="block text-sm text-gray-600 mb-1">Password</label>
            <input
              id="invite-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="new-password"
            />
          </div>

          <div>
            <label htmlFor="invite-confirm" className="block text-sm text-gray-600 mb-1">Confirm password</label>
            <input
              id="invite-confirm"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={`w-full py-2 rounded-md text-white transition duration-200 ${
              submitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {submitting ? 'Creating account...' : 'Create account and join group'}
          </button>
        </form>
      </>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-md border border-gray-200">
        {renderBody()}
      </div>
    </div>
  );
}
